import React, { useCallback, useEffect, useMemo, useState } from "react";
import mixpanel from "mixpanel-browser";

import Dialog, { CloseDialog } from "components/Dialog";
import ToggleMulti from "components/ToggleMulti";
import UsageBar from "components/UsageBar";
import LegacyCredits from "./LegacyCredits";
import NewCredits from "./NewCredits";

import { usePaymentModal } from "contexts/PaymentModalContext.jsx";
import { RUNS_PER_CREDIT } from "utils/parametric/constants";
import { getUnlockRunsRequirement } from "utils/parametric";
import { TRANSACTIONS_TYPES, URL } from "utils/payment/constants";

import classes from "./styles.module.scss";

import VoltaCreditBlue from "assets/images/icons/VoltaCreditBlue.svg";

const formatCount = (value) => Number(value || 0).toLocaleString();

const UnlockRunsModal = ({
    onClose,
    isOpen,
    legacyTotalCredits = 0,
    newTotalCredits = 0,
    subjectId,
    subjectName,
    userUid,
    availableRuns = 0,
    totalCombinations = 0,
    runsUsedTotal = 0,
    runsToBeUsed = 0,
    runsPerCredit = RUNS_PER_CREDIT,
    transactionType = TRANSACTIONS_TYPES["SPENT.PARAMETRIC"],
    spentFeature = "PARAMETRIC",
    spentUrl = URL.PARAMETRIC,
    /** Mixpanel event prefix, also used in the copy, e.g. "Parametric" / "Community". */
    analyticsLabel = "Parametric",
    subjectLabel = "analysis",
    capacityTitle = "Analysis capacity",
    allowLegacyCredits = true,
}) => {
    const { dispatch: paymentModalDispatch } = usePaymentModal();
    const [selectedCredits, setSelectedCredits] = useState("new");
    const [previewLegacyRuns, setPreviewLegacyRuns] = useState(0);

    const { shortfallRuns, creditsNeeded, hasEnoughUnlocked } = useMemo(
        () =>
            getUnlockRunsRequirement({
                availableRuns,
                totalCombinations,
                runsPerCredit,
            }),
        [availableRuns, totalCombinations, runsPerCredit]
    );

    const [previewCreditsToSpend, setPreviewCreditsToSpend] = useState(() => (creditsNeeded > 0 ? creditsNeeded : 1));

    const hasLegacyCredits = allowLegacyCredits && legacyTotalCredits > 0;
    const showingLegacy = hasLegacyCredits && selectedCredits === "legacy";

    useEffect(() => {
        if (!isOpen) return;
        setPreviewCreditsToSpend(creditsNeeded > 0 ? creditsNeeded : 1);
        setPreviewLegacyRuns(Math.min(Math.max(shortfallRuns, 0), legacyTotalCredits));
        mixpanel.track(`${analyticsLabel} Unlock Runs Open`, {
            UserId: userUid,
            AnalysisId: subjectId,
            AvailableRuns: availableRuns,
            TotalCombinations: totalCombinations,
            CreditsNeeded: creditsNeeded,
        });
    }, [isOpen, creditsNeeded, shortfallRuns, legacyTotalCredits]);

    const previewUnlocked = showingLegacy
        ? availableRuns + previewLegacyRuns
        : availableRuns + previewCreditsToSpend * runsPerCredit;

    const handleCreditsToSpendChange = useCallback((value) => {
        setPreviewCreditsToSpend(Number(value) || 0);
    }, []);

    const handleLegacyRunsChange = useCallback((value) => {
        setPreviewLegacyRuns(Number(value) || 0);
    }, []);

    const openBuyCredits = () => {
        paymentModalDispatch({ type: "OPEN" });
    };

    const capacityHint = hasEnoughUnlocked ? (
        <p className={classes.capacityHint}>
            Current unlocked capacity covers this {subjectLabel} (<strong>{formatCount(totalCombinations)}</strong>{" "}
            total runs).
        </p>
    ) : (
        <p className={`${classes.capacityHint} ${classes.capacityHintWarn}`}>
            You still need <strong>{formatCount(shortfallRuns)}</strong> unlocked runs for the current {subjectLabel}
            {showingLegacy
                ? ` (${formatCount(shortfallRuns)} legacy credits, 1:1).`
                : ` (${formatCount(creditsNeeded)} credit${creditsNeeded === 1 ? "" : "s"} at ${formatCount(
                      runsPerCredit
                  )} runs each).`}
        </p>
    );

    return (
        <Dialog open={isOpen} closeActions={onClose} classes={{ paperClass: classes.buyMoreRunsModalContainer }}>
            <CloseDialog closeActions={onClose} />
            <h2 className={classes.buyRunsTitle}>Unlock Runs</h2>
            <p className={classes.buyRunsIntro}>
                Spend credits to unlock runs on this {subjectLabel}. Unlocked runs are the maximum number of simulations
                this {subjectLabel} can include.
            </p>

            <div className={classes.capacityCard}>
                <div className={classes.capacityCardHeader}>
                    <p className={classes.capacityCardTitle}>{capacityTitle}</p>
                    <p className={classes.capacityNeed}>
                        Needs <strong>{formatCount(totalCombinations)}</strong> total
                    </p>
                </div>
                <UsageBar
                    usage={runsUsedTotal}
                    toBeUsed={runsToBeUsed}
                    total={previewUnlocked}
                    mainColor="#18C1AD"
                    secondaryColor="#FFB22D"
                    showDetails
                />
                {capacityHint}
                {previewUnlocked !== availableRuns && (
                    <p className={classes.capacityHint}>
                        After this spend, unlocked runs go from <strong>{formatCount(availableRuns)}</strong> to{" "}
                        <strong>{formatCount(previewUnlocked)}</strong>
                        {previewUnlocked < totalCombinations
                            ? ` — still short of what this ${subjectLabel} needs.`
                            : previewUnlocked > totalCombinations
                              ? ` — enough for this ${subjectLabel}, with some spare capacity.`
                              : ` — enough for this ${subjectLabel}.`}
                    </p>
                )}
            </div>

            <div className={classes.creditCountContainer}>
                {hasLegacyCredits && (
                    <p className={classes.creditCount}>
                        Legacy credits: {formatCount(legacyTotalCredits)} <img src={VoltaCreditBlue} alt="" />
                    </p>
                )}
                <p className={classes.creditCount}>
                    Credits: {formatCount(newTotalCredits)} <img src={VoltaCreditBlue} alt="" />
                </p>
                {!showingLegacy && newTotalCredits < creditsNeeded && (
                    <button type="button" className={classes.buyCreditsLink} onClick={openBuyCredits}>
                        Buy Credits
                    </button>
                )}
            </div>

            {hasLegacyCredits && (
                <ToggleMulti
                    className={classes.creditTypeToggle}
                    options={[
                        {
                            label: "Legacy credits",
                            id: "legacy",
                            onClick: () => setSelectedCredits("legacy"),
                        },
                        {
                            label: "Credits",
                            id: "new",
                            onClick: () => setSelectedCredits("new"),
                        },
                    ]}
                    active={selectedCredits}
                />
            )}

            <div className={classes.creditTransactionContainer}>
                {showingLegacy ? (
                    <LegacyCredits
                        legacyTotalCredits={legacyTotalCredits}
                        onClose={onClose}
                        parametricId={subjectId}
                        userUid={userUid}
                        shortfallRuns={shortfallRuns}
                        availableRuns={availableRuns}
                        onRunsToUnlockChange={handleLegacyRunsChange}
                    />
                ) : (
                    <NewCredits
                        newTotalCredits={newTotalCredits}
                        onClose={onClose}
                        spentId={subjectId}
                        spentName={subjectName}
                        creditsNeeded={creditsNeeded}
                        shortfallRuns={shortfallRuns}
                        availableRuns={availableRuns}
                        onCreditsToSpendChange={handleCreditsToSpendChange}
                        runsPerCredit={runsPerCredit}
                        transactionType={transactionType}
                        spentFeature={spentFeature}
                        spentUrl={spentUrl}
                        analyticsLabel={analyticsLabel}
                        subjectLabel={subjectLabel}
                    />
                )}
            </div>
        </Dialog>
    );
};

export default UnlockRunsModal;
